import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import {
  type ExamType,
  isProcedureExamType,
  US_EXAM_TYPES,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-review";
import { useUser } from "$global-providers/user-provider/provider";
import { useCallback, useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import {
  CREDENTIALING_THRESHOLDS,
  type CredStatus,
  computeCredStatus,
} from "./credentialing-config";
import { useCan } from "./rbac/use-can";
import { isAgreeingReview } from "./review-agreement";
import { MATCH_NOTHING } from "./rxdb-selectors";
import {
  getRange,
  getTrendBuckets,
  isWithinRange,
  type StatsFilter,
} from "./stats-time-filter";
import {
  useEnabledExamTypes,
  useWorksheetSetExamTypes,
} from "./use-enabled-exam-types";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExamTypeStats = {
  examType: ExamType;
  rangeScore: string;
  rangeTrend: number[];
  allTimeExamCount: number;
  allTimeScore: string;
  /** Required submitted exams for credentialing (from config) */
  credRequired: number;
  /** Minimum average quality score required for credentialing (from config) */
  avgScoreMin: number;
  credStatus: CredStatus;
};

export type RecentExam = {
  examId: string;
  encounterId: string;
  date: string;
  patientName: string | null;
  examType: ExamType;
  status: "reviewed" | "pending";
  score: string;
};

export type PerformerStats = {
  overallQaScore: string;
  agreementRate: number | null;
  scoreTrend: number[];
  volume: number;
  volumeTrend: number[];
  topFeedbackTags: Array<{ tag: string; pct: number }>;
  byExamType: ExamTypeStats[];
  recentExams: RecentExam[];
  hasExamsInRange: boolean;
  hasAnyData: boolean;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const EXAM_TYPE_ORDER: ExamType[] = [
  "efast",
  "cardiac",
  "lung",
  "bladder",
  "renal",
  "aorta",
  "pregnancy",
  "msk",
  "guided_joint_injection",
  "guided_tendon_injection",
  "guided_nerve_block",
  ...US_EXAM_TYPES,
];

const RECENT_EXAM_LIMIT = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const averageOrNull = (scores: number[]): number | null =>
  scores.length > 0
    ? scores.reduce((sum, s) => sum + s, 0) / scores.length
    : null;

const formatScore = (scores: number[]): string =>
  averageOrNull(scores)?.toFixed(1) ?? "—";

const toPct = (count: number, total: number): number | null =>
  total > 0 ? Math.round((count / total) * 100) : null;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const usePerformerStats = (
  performerId: string,
  filter: StatsFilter,
): PerformerStats => {
  const { id: currentUserId } = useUser();
  const canViewProgram = useCan("viewProgramDashboard");
  const canRead = performerId === currentUserId || canViewProgram;
  const enabledExamTypes = useEnabledExamTypes();
  const worksheetSetExamTypes = useWorksheetSetExamTypes();

  // Gating only here is load-bearing: the exam and review queries below derive
  // their selectors from the ids this one returns, so widening either of their
  // empty-case sentinels to an unfiltered find() would leak peer records.
  const encountersQuery = useCallback(
    (collection: RxCollection<EpocusEncounter>) =>
      collection.find({
        selector: canRead
          ? { performerId: { $eq: performerId } }
          : MATCH_NOTHING,
      }),
    [performerId, canRead],
  );
  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    encountersQuery,
  );

  const encounterIds = useMemo(
    () => encounters.map((encounter) => encounter.id),
    [encounters],
  );

  const examsQuery = useCallback(
    (collection: RxCollection<EpocusExam>) =>
      encounterIds.length > 0
        ? collection.find({ selector: { encounterId: { $in: encounterIds } } })
        : collection.find({ selector: { encounterId: { $eq: "__none__" } } }),
    [encounterIds],
  );
  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    examsQuery,
  );

  const examIds = useMemo(() => exams.map((exam) => exam.id), [exams]);

  const reviewsQuery = useCallback(
    (collection: RxCollection<EpocusReview>) =>
      examIds.length > 0
        ? collection.find({ selector: { examId: { $in: examIds } } })
        : collection.find({ selector: { examId: { $eq: "__none__" } } }),
    [examIds],
  );
  const { result: reviews } = useRxData<EpocusReview>(
    RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
    reviewsQuery,
  );

  return useMemo(() => {
    const now = Date.now();
    const range = getRange(filter.range, now);

    const myEncounters = encounters;
    const encounterById = new Map(myEncounters.map((e) => [e.id, e]));

    const mySubmittedExams = exams.filter((e) => e.status === "submitted");
    const examById = new Map(mySubmittedExams.map((e) => [e.id, e]));
    const myReviews = reviews.filter((r) => examById.has(r.examId));

    const examTime = (examId: string): number | null => {
      const exam = examById.get(examId);
      const date = exam ? encounterById.get(exam.encounterId)?.date : undefined;
      if (date === undefined) return null;
      const time = new Date(date).getTime();
      return Number.isFinite(time) ? time : null;
    };

    const rangeExams = mySubmittedExams.filter((exam) => {
      const time = examTime(exam.id);
      return time !== null && isWithinRange(time, range);
    });
    const rangeExamIds = new Set(rangeExams.map((e) => e.id));
    const rangeReviews = myReviews.filter((r) => rangeExamIds.has(r.examId));
    const rangeEncounters = myEncounters.filter((e) =>
      isWithinRange(e.date, range),
    );

    const reviewsByExamId = new Map<string, EpocusReview[]>();
    for (const review of myReviews) {
      const list = reviewsByExamId.get(review.examId);
      if (list) list.push(review);
      else reviewsByExamId.set(review.examId, [review]);
    }

    const encounterTimes = myEncounters
      .map((e) => new Date(e.date).getTime())
      .filter((t) => Number.isFinite(t));
    const earliest =
      encounterTimes.length > 0 ? Math.min(...encounterTimes) : null;
    const buckets = getTrendBuckets(filter, now, earliest);

    const volumeTrend = buckets.map(
      ({ startInclusive, endExclusive }) =>
        rangeEncounters.filter((e) => {
          const time = new Date(e.date).getTime();
          return time >= startInclusive && time < endExclusive;
        }).length,
    );

    const bucketedScores = (scoped: EpocusReview[]): number[] =>
      buckets
        .map(({ startInclusive, endExclusive }) =>
          averageOrNull(
            scoped
              .filter((r) => {
                const time = examTime(r.examId);
                return (
                  time !== null && time >= startInclusive && time < endExclusive
                );
              })
              .map((r) => r.qualityScore),
          ),
        )
        // Coercing an empty bucket to 0 would draw the trend line to the floor
        // and read as a quality collapse rather than absent data.
        .filter((score): score is number => score !== null);

    const tagCounts = new Map<string, number>();
    for (const review of rangeReviews) {
      for (const tag of review.feedbackTags) {
        tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
      }
    }
    const topFeedbackTags = [...tagCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([tag, count]) => ({
        tag,
        pct:
          rangeReviews.length > 0
            ? Math.round((count / rangeReviews.length) * 100)
            : 0,
      }));

    const scoresByType = (scopedExams: EpocusExam[]) => {
      const byType = new Map<ExamType, number[]>();
      for (const exam of scopedExams) {
        const scores = (reviewsByExamId.get(exam.id) ?? []).map(
          (r) => r.qualityScore,
        );
        if (scores.length === 0) continue;
        const existing = byType.get(exam.examType);
        if (existing) existing.push(...scores);
        else byType.set(exam.examType, scores);
      }
      return byType;
    };

    const rangeScoresByType = scoresByType(rangeExams);
    const allTimeScoresByType = scoresByType(mySubmittedExams);

    const allTimeCountByType = new Map<ExamType, number>();
    for (const exam of mySubmittedExams) {
      allTimeCountByType.set(
        exam.examType,
        (allTimeCountByType.get(exam.examType) ?? 0) + 1,
      );
    }

    const byExamType: ExamTypeStats[] = EXAM_TYPE_ORDER.filter(
      (examType) =>
        (worksheetSetExamTypes.includes(examType) &&
          !isProcedureExamType(examType)) ||
        enabledExamTypes.includes(examType) ||
        allTimeCountByType.has(examType),
    ).map((examType) => {
      const allTimeScores = allTimeScoresByType.get(examType) ?? [];
      const allTimeExamCount = allTimeCountByType.get(examType) ?? 0;
      const thresholds = CREDENTIALING_THRESHOLDS[examType];

      return {
        examType,
        rangeScore: formatScore(rangeScoresByType.get(examType) ?? []),
        rangeTrend: bucketedScores(
          rangeReviews.filter(
            (r) => examById.get(r.examId)?.examType === examType,
          ),
        ),
        allTimeExamCount,
        allTimeScore: formatScore(allTimeScores),
        credRequired: thresholds.totalExamsMin,
        avgScoreMin: thresholds.avgScoreMin,
        credStatus: computeCredStatus(
          allTimeExamCount,
          averageOrNull(allTimeScores),
          thresholds,
        ),
      };
    });

    const recentExams: RecentExam[] = rangeExams
      .map((exam) => {
        const encounter = encounterById.get(exam.encounterId);
        const examReviews = reviewsByExamId.get(exam.id) ?? [];
        return {
          examId: exam.id,
          encounterId: exam.encounterId,
          date: encounter?.date ?? "",
          patientName: encounter?.patientName ?? null,
          examType: exam.examType,
          status: examReviews.length > 0 ? "reviewed" : "pending",
          score: formatScore(examReviews.map((r) => r.qualityScore)),
        } satisfies RecentExam;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, RECENT_EXAM_LIMIT);

    return {
      overallQaScore: formatScore(rangeReviews.map((r) => r.qualityScore)),
      agreementRate: toPct(
        rangeReviews.filter(isAgreeingReview).length,
        rangeReviews.length,
      ),
      scoreTrend: bucketedScores(rangeReviews),
      volume: rangeEncounters.length,
      volumeTrend,
      topFeedbackTags,
      byExamType,
      recentExams,
      hasExamsInRange: rangeExams.length > 0,
      hasAnyData: myEncounters.length > 0,
    };
  }, [
    encounters,
    exams,
    reviews,
    filter,
    enabledExamTypes,
    worksheetSetExamTypes,
  ]);
};
